/* eslint-disable */
import React, { useRef, useState, useEffect } from 'react';
import cls from 'classnames';
import PropTypes from 'prop-types';
import { gsap, TweenMax, Linear, CSSRulePlugin } from 'gsap/all';
import useAnimationFrame from '~/hooks/useAnimationFrame';
import useCursorPosition from '~/hooks/useCursorPosition';
import css from './Cursor.scss';

gsap.registerPlugin(CSSRulePlugin);

const classes = {
	cursor: css.cursor,
	cursorInner: css.cursorInner,
	cursorOuter: css.cursorOuter,
	cursorProgress: css.cursorProgress,
	isLoading: css.isLoading,
	isHidden: css.isHidden,
};

const propTypes = {
	className: PropTypes.string,
	classes: PropTypes.objectOf(PropTypes.string),
};

const defaultProps = {
	className: '',
	classes,
};

export const Cursor = ({ className, classes, ...props }) => {
	if ('ontouchstart' in window) return null;

	const innerRef = useRef();
	const hoverRef = useRef();
	const outerSpeed = useRef(0);
	const [outerRef, cursorInfo] = useCursorPosition();
	const [isLoading, setIsLoading] = useState(false);
	const [isHidden, setIsHidden] = useState(true);
	const [stuck, setStuck] = useState(false);
	const [frame, setFrame] = useState(0);

	const show = () => {
		if (isHidden && cursorInfo.clientX) setIsHidden(false);
	};

	const updatePosition = () => {
		TweenMax.set(innerRef.current, {
			x: cursorInfo.clientX,
			y: cursorInfo.clientY,
		});
		TweenMax.set(outerRef.current, {
			x: cursorInfo.clientX - cursorInfo.rect.width * 0.5,
			y: cursorInfo.clientY - cursorInfo.rect.height * 0.5,
		});
		show();
	};

	useEffect(updatePosition, [cursorInfo.clientX, cursorInfo.clientY]);

	useEffect(() => {
		const sidebarAnchorTween = TweenMax.to(outerRef.current, 0.2, {
			paused: true,
			ease: Linear.easeOut,
			backgroundColor: '#fff',
		});

		const cursorPressedTween = TweenMax.to(outerRef.current, 0.2, {
			paused: true,
			backgroundColor: '#fff',
			borderColor: 'transparent',
			scale: 2.1,
		});

		const anchorsIn = (evt) => {
			setStuck(true);
			outerSpeed.current = 0;
			TweenMax.set(innerRef.current, { opacity: 0 });
			sidebarAnchorTween.play();
		};

		const anchorsOut = (evt) => {
			setStuck(false);
			outerSpeed.current = 0.2;
			TweenMax.set(innerRef.current, { opacity: 1 });
			sidebarAnchorTween.reverse();
		};

		const handleMouseDown = (evt) => {
			cursorPressedTween.play();
		};

		const handleMouseUp = (evt) => {
			cursorPressedTween.reverse();
		};

		const handleMouseEnter = (evt) => {
			const name = evt.currentTarget.getAttribute('data-ui-cursor');
			switch (name) {
				case 'sidebar-anchor': return anchorsIn(evt);
				default: return anchorsIn(evt);
			}
		};

		const handleMouseLeave = (evt) => {
			const name = evt.currentTarget.getAttribute('data-ui-cursor');
			switch (name) {
				case 'sidebar-anchor': return anchorsOut(evt);
				default: return anchorsOut(evt);
			}
		};

		const handleHoverEvents = (action) => (item) => {
			item[`${action}EventListener`]('mouseenter', handleMouseEnter, true);
			item[`${action}EventListener`]('mouseleave', handleMouseLeave, true);
		};

		const arrayFrom = (nodelist) => {
			return Array.prototype.slice.call(nodelist);
		};

		setTimeout(() => outerSpeed.current = 0.2, 100);
		hoverRef.current = arrayFrom(document.querySelectorAll('[data-ui-cursor]'));
		hoverRef.current.forEach(handleHoverEvents('add'));
		document.addEventListener('pointerdown', handleMouseDown, true);
		document.addEventListener('pointerup', handleMouseUp, true);

		return () => {
			hoverRef.current.forEach(handleHoverEvents('remove'));
			document.removeEventListener('pointerdown', handleMouseDown, true);
			document.removeEventListener('pointerup', handleMouseUp, true);
		};
	}, []);

	useAnimationFrame((deltaTime) => {
		setFrame((prevFrame) => (prevFrame + deltaTime * 0.01) % 100);
	});

	return (
		<React.Fragment>
			<div
				ref={outerRef}
				aria-hidden={true}
				className={cls(classes.cursor, classes.cursorOuter, className, {
					[classes.isHidden]: isHidden,
				})}
			/>
			<div
				ref={innerRef}
				aria-hidden={true}
				className={cls(classes.cursor, classes.cursorInner, className, {
					[classes.isHidden]: isHidden,
				})}
			/>
		</React.Fragment>
	);
};

Cursor.displayName = 'Cursor';
Cursor.propTypes = propTypes;
Cursor.defaultProps = defaultProps;
export default Cursor;
